import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { INTERNAL_TAG } from '../config/tagConfig';
import { generateSlug } from '../utils/generateSlug';

import type { Notification, WhatsNewFeedJson } from '../types/WhatsNew.types';

const IMAGE_BASE_URL = 'https://docs.bozic.io/pages/lia/release-notes/';
const DOCS_BASE_URL = 'https://docs.bozic.io';

function extractReleaseNotesHtml(rawHtml: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(rawHtml, 'text/html');

  const images = doc.getElementsByTagName('img');
  for (const img of images) {
    const src = img.getAttribute('src');
    if (src && !src.startsWith('http')) {
      img.setAttribute('src', `${IMAGE_BASE_URL}${src}`);
    }
  }

  const main = doc.getElementsByTagName('main')[0];
  const firstDiv = main?.getElementsByTagName('div')[0];
  return firstDiv?.innerHTML ?? '';
}

export const whatsNewApi = createApi({
  reducerPath: 'whatsNewApi',
  baseQuery: fetchBaseQuery({ baseUrl: DOCS_BASE_URL }),
  endpoints: build => ({
    getWhatsNewFeed: build.query<Notification[], void>({
      query: () => '/feed.json',
      transformResponse: (response: WhatsNewFeedJson): Notification[] =>
        response.items
          .filter(item => item.tags.includes(INTERNAL_TAG))
          .map(item => {
            const parser = new DOMParser();
            const doc = parser.parseFromString(item.content_html, 'text/html');
            const firstAnchor = doc.querySelector('a');
            const releaseNotesUrl = firstAnchor?.href ?? item.url;

            return {
              id: item.id,
              title: item.title,
              contentHtml: item.content_html,
              datePublished: item.date_published,
              tags: item.tags,
              url: item.url,
              releaseNotesUrl,
              slug: generateSlug(item.id)
            };
          })
    }),
    getReleaseNotesPage: build.query<string, string>({
      query: url => ({
        url,
        responseHandler: 'text'
      }),
      transformResponse: (rawHtml: string): string => extractReleaseNotesHtml(rawHtml)
    })
  })
});

export const { useGetWhatsNewFeedQuery, useGetReleaseNotesPageQuery } = whatsNewApi;
